import { ArrowDownUp, ArrowUpDown, Download } from "lucide-react";
import moment from "moment";
import React, { useEffect, useState, useRef } from "react";
import ReactPaginate from "react-paginate";
import { useSelector } from "react-redux";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import BaseUrl from "../../../constants/BaseUrl";
import ParagraphTag from "../../reusebale/Paragraph";
import ImageTag from "../../reusebale/imageTag";
import NoData from "../../reusebale/noData";
import LeaseTableLoader from "../../skeleton-loader/lease-table";
import InvoiceTemplate from "./invoice-template";

const InvoiceListing = () => {
  const { loginSuccess } = useSelector((state) => state.auth);
  const { userCompleteProfile } = useSelector((state) => state.auth);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const invoiceRef = useRef(null);

  // PDF Download Function
  const handleDownloadInvoice = async (invoice) => {
    if (!userCompleteProfile?.personalInformation) {
      console.error("User profile information not available");
      return;
    }

    setDownloadingInvoiceId(invoice._id);

    try {
      // Create a temporary container for the invoice template
      const tempContainer = document.createElement("div");
      tempContainer.style.position = "absolute";
      tempContainer.style.left = "-9999px";
      tempContainer.style.top = "0";
      document.body.appendChild(tempContainer);

      // Create the React element and render it to the temp container
      const { createRoot } = await import("react-dom/client");
      const root = createRoot(tempContainer);

      // Create a promise to wait for the component to render
      await new Promise((resolve) => {
        root.render(
          React.createElement(InvoiceTemplate, {
            ref: invoiceRef,
            invoice: invoice,
            landlordInfo: userCompleteProfile.personalInformation,
          })
        );

        // Wait a bit for rendering to complete
        setTimeout(resolve, 100);
      });

      // Get the rendered element
      const element = tempContainer.firstChild;

      if (element) {
        // Generate canvas from the element
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          width: element.scrollWidth,
          height: element.scrollHeight,
        });

        // Create PDF
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        const imgWidth = 210; // A4 width in mm
        const pageHeight = 295; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        // Add first page
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        // Add additional pages if content is longer than one page
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        // Generate filename
        const invoiceNumber = `INV-${invoice._id?.slice(-8)}`;
        const fileName = `${invoiceNumber}_${moment(invoice.createdAt).format(
          "MM/DD/YYYY"
        )}.pdf`;

        // Download the PDF
        pdf.save(fileName);
      }

      // Cleanup
      root.unmount();
      document.body.removeChild(tempContainer);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const itemsPerPage = 10;

  const GetAllInvoices = React.useCallback(() => {
    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);
    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    };

    fetch(
      `${BaseUrl}landlord/profile/getAllSubscriptions/${loginSuccess.user._id}`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        console.log("API Response:", result); // Debug log
        if (result.status === "Success" && result.data) {
          setInvoices(result.data);
        } else {
          console.error("API Error:", result.message || "Unknown error");
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Fetch Error:", error);
        setLoading(false);
      });
  }, [loginSuccess?.token, loginSuccess?.user?._id]);

  // Handle sorting
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Sort invoices based on sortConfig
  const sortedInvoices = [...invoices].sort((a, b) => {
    if (sortConfig.key === "amount") {
      return sortConfig.direction === "asc"
        ? a.amount - b.amount
        : b.amount - a.amount;
    }
    if (sortConfig.key === "invoiceNumber") {
      return sortConfig.direction === "asc"
        ? a.invoiceNumber - b.invoiceNumber
        : b.invoiceNumber - a.invoiceNumber;
    }
    if (sortConfig.key === "createdAt" || sortConfig.key === "startDate") {
      const dateA = new Date(a[sortConfig.key]);
      const dateB = new Date(b[sortConfig.key]);
      return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
    }
    if (typeof a[sortConfig.key] === "string") {
      return sortConfig.direction === "asc"
        ? a[sortConfig.key].localeCompare(b[sortConfig.key])
        : b[sortConfig.key].localeCompare(a[sortConfig.key]);
    }
    return 0;
  });

  // Paginate data
  const paginatedData = sortedInvoices.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const handlePageClick = (selectedPage) => {
    setCurrentPage(selectedPage.selected);
  };

  // Get status badge based on subscription plan name
  const getStatusBadge = (invoice) => {
    const { subscriptionPlanName } = invoice;

    // Check if it's an add-on based on the plan name
    if (subscriptionPlanName && subscriptionPlanName.includes("Add-on")) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Property Add-on
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          First Purchase
        </span>
      );
    }
  };

  // Derive display status — transaction is always the source of truth
  const getDisplayStatus = (invoice) => {
    if (invoice._transactionStatus) {
      const txn = invoice._transactionStatus.toLowerCase();
      if (txn === "completed") return "active";
      if (txn === "failed") return "failed";
      return "processing";
    }
    if (invoice.status === "active") return "active";
    if (invoice.status === "invalid") return "invalid";
    return "pending";
  };

  // Get payment status badge
  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case "invalid":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Invalid
          </span>
        );
      case "active":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            Active
          </span>
        );
      case "processing":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            Processing
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };

  useEffect(() => {
    GetAllInvoices();
  }, [GetAllInvoices]);

  return (
    <div className="h-full pt-5 w-full">
      <div className="bg-white w-full h-[87%] rounded-lg px-4 py-3">
        <div className=" h-full">
          <div className="flex justify-between items-center h-[12%]">
            <ParagraphTag
              content="Invoice Listing"
              classes="text-darkColor font-popinsSemiBold text-lg py-5"
            />
          </div>

          <div className="overflow-x-auto w-full h-[88%] mt-1">
            <table className="w-full">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b border-black/20">
                  <th className="w-[20%] text-left pb-2">
                    <span className="flex">
                      <ParagraphTag
                        content="Invoice Details"
                        classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                      />
                      {sortConfig.key === "invoiceNumber" ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("invoiceNumber")}
                          />
                        ) : (
                          <ArrowDownUp
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("invoiceNumber")}
                          />
                        )
                      ) : (
                        <ArrowUpDown
                          className="cursor-pointer"
                          size={13}
                          color="#6F6F6F"
                          onClick={() => requestSort("invoiceNumber")}
                        />
                      )}
                    </span>
                  </th>

                  <th className="w-[15%] pb-2">
                    <span className="flex justify-center">
                      <ParagraphTag
                        content="Plan Details"
                        classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                      />
                    </span>
                  </th>

                  <th className="w-[12%] pb-2">
                    <span className="flex justify-center">
                      <ParagraphTag
                        content="Type"
                        classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                      />
                      {sortConfig.key === "subscriptionPlanName" ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("subscriptionPlanName")}
                          />
                        ) : (
                          <ArrowDownUp
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("subscriptionPlanName")}
                          />
                        )
                      ) : (
                        <ArrowUpDown
                          className="cursor-pointer"
                          size={13}
                          color="#6F6F6F"
                          onClick={() => requestSort("subscriptionPlanName")}
                        />
                      )}
                    </span>
                  </th>

                  <th className="w-[10%] pb-2">
                    <span className="flex justify-center">
                      <ParagraphTag
                        content="Amount"
                        classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                      />
                      {sortConfig.key === "amount" ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("amount")}
                          />
                        ) : (
                          <ArrowDownUp
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("amount")}
                          />
                        )
                      ) : (
                        <ArrowUpDown
                          className="cursor-pointer"
                          size={13}
                          color="#6F6F6F"
                          onClick={() => requestSort("amount")}
                        />
                      )}
                    </span>
                  </th>

                  <th className="w-[12%] pb-2">
                    <span className="flex justify-center">
                      <ParagraphTag
                        content="Payment Status"
                        classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                      />
                      {sortConfig.key === "status" ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("status")}
                          />
                        ) : (
                          <ArrowDownUp
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("status")}
                          />
                        )
                      ) : (
                        <ArrowUpDown
                          className="cursor-pointer"
                          size={13}
                          color="#6F6F6F"
                          onClick={() => requestSort("status")}
                        />
                      )}
                    </span>
                  </th>

                  <th className="w-[12%] pb-2">
                    <span className="flex justify-center">
                      <ParagraphTag
                        content="Subscription Period"
                        classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                      />
                    </span>
                  </th>

                  <th className="w-[7%] text-right pr-4 pb-2">
                    <ParagraphTag
                      content="Actions"
                      classes="text-darkColor/40 font-popinsSemiBold text-xs"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((invoice) => (
                  <tr
                    key={invoice._id}
                    className="hover:bg-gray-100 rounded-md cursor-pointer"
                  >
                    <td className="py-2">
                      <div className="flex items-center">
                        <div className="w-[18%] rounded-full">
                          <ImageTag
                            path="/assets/daashboard/payment.png"
                            classes="w-full"
                            altText="invoice"
                          />
                        </div>
                        <div className="w-[78%] pl-3">
                          <ParagraphTag
                            content={`INV-${invoice._id?.slice(-8)}`}
                            classes="text-darkColor/50 font-popinsMedium text-xs"
                          />
                          <ParagraphTag
                            content={invoice.subscriptionPlanName}
                            classes="text-darkColor font-popinsSemiBold text-sm"
                          />
                        </div>
                      </div>
                    </td>

                    <td className="py-2 px-4 font-popinsMedium text-darkColor text-sm text-center">
                      <div className="flex flex-col">
                        <span className="text-xs text-darkColor">
                          {invoice.packageData?.allowProperties} Properties
                        </span>
                        <span className="text-xs text-darkColor">
                          {invoice.subscriptionType}
                        </span>
                      </div>
                    </td>

                    <td className="py-2 px-4 font-popinsMedium text-darkColor text-sm text-center">
                      {getStatusBadge(invoice)}
                    </td>

                    <td className="py-2 px-4 font-popinsMedium text-darkColor text-sm text-center">
                      <div className="flex flex-col">
                        <span className="text-lg font-popinsSemiBold text-darkColor">
                          ${invoice.amount.toLocaleString()}
                        </span>
                        {invoice.packageData?.discountAmount &&
                          invoice.packageData.discountAmount !== "$0" && (
                            <span className="text-xs text-green-600">
                              Discount: {invoice.packageData.discountAmount}
                            </span>
                          )}
                      </div>
                    </td>

                    <td className="py-2 px-4 font-popinsMedium text-darkColor text-sm text-center">
                      {getPaymentStatusBadge(getDisplayStatus(invoice))}
                    </td>

                    <td className="py-2 px-4 font-popinsMedium text-darkColor text-sm text-center">
                      <div className="flex flex-col">
                        <span>
                          {moment(invoice.startDate).format("MM/DD/YYYY")}
                        </span>
                        <span className="text-xs text-darkColor/50">-</span>
                        <span>
                          {moment(invoice.endDate).format("MM/DD/YYYY")}
                        </span>
                      </div>
                    </td>

                    <td className="py-2 px-4 font-popinsMedium text-darkColor text-sm">
                      <div className="flex justify-end space-x-2">
                        <button
                          className="flex items-center justify-center border border-green-500 w-8 h-8 rounded-md cursor-pointer hover:bg-green-500 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Download Invoice"
                          onClick={() => handleDownloadInvoice(invoice)}
                          disabled={downloadingInvoiceId === invoice._id}
                        >
                          {downloadingInvoiceId === invoice._id ? (
                            <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full"></div>
                          ) : (
                            <Download size={16} color="currentColor" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {loading && <LeaseTableLoader />}

            {paginatedData.length === 0 && !loading && (
              <div className="w-full flex justify-center mt-5">
                <NoData />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex justify-center h-[13%] items-center">
        <ReactPaginate
          previousLabel="Previous"
          nextLabel="Next"
          breakLabel="..."
          pageCount={Math.ceil(sortedInvoices.length / itemsPerPage)}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={handlePageClick}
          containerClassName="flex space-x-2 items-center"
          pageClassName="px-3 py-1 border rounded-md text-sm font-popinsMedium cursor-pointer"
          previousClassName="px-3 py-1 border rounded-l-md text-sm cursor-pointer font-popinsMedium"
          nextClassName="px-3 py-1 border rounded-r-md text-sm font-popinsMedium cursor-pointer font-popinsMedium"
          disabledClassName="opacity-50 !cursor-not-allowed"
          activeClassName="bg-themeColor text-white"
          breakClassName="px-3 py-1 text-sm font-popinsMedium"
        />
      </div>
    </div>
  );
};

export default InvoiceListing;
